import CloseIcon from '@mui/icons-material/Close';

import { SxProps, Theme } from '@mui/material';
import React from 'react';

import { Dialog } from '_lib/components/Dialog/Dialog';
import { DialogTitle } from '_lib/components/DialogTitle/DialogTitle';
import { IconButton } from '_lib/components/IconButton/IconButton';
import { Stack } from '_lib/components/Stack/Stack';

import { AsyncDialogProps } from '_lib/hooks/useAsyncDialog';
import { toSxArray } from '_lib/utils/helpers';

export type DialogVariant = 'primary' | 'warning' | 'error';

export const getColorsForVariant = (theme: Theme, variant?: DialogVariant) => {
  if (!variant || variant === 'primary')
    return { backgroundColor: theme.palette.primary.main, color: theme.palette.primary.contrastText };
  else if (variant === 'warning')
    return { backgroundColor: theme.palette.warning.main, color: theme.palette.warning.contrastText };
  else return { backgroundColor: theme.palette.error.main, color: theme.palette.error.contrastText };
};

interface DialogWrapperProps<T> extends AsyncDialogProps<T> {
  children: React.ReactNode;
  variant?: DialogVariant;
  sx?: SxProps<Theme>;
}

export const DialogWrapper = <T,>(props: DialogWrapperProps<T>): React.ReactElement => {
  const { titleText, children, onClose, variant, sx, dismissable: propsDismissable, ...dialogProps } = props;

  const handleClose = () => {
    onClose();
  };

  const dismissable = typeof propsDismissable === 'boolean' ? propsDismissable : true;

  return (
    <Dialog
      onClose={handleClose}
      open={!!dialogProps.open}
      sx={
        dialogProps.container
          ? [
              ...toSxArray(sx),
              {
                position: 'absolute',
                '& .MuiBackdrop-root.MuiModal-backdrop': {
                  position: 'absolute'
                }
              }
            ]
          : toSxArray(sx)
      }
      dismissable={dismissable}
      {...dialogProps}>
      <DialogTitle variant={variant} sx={{ borderTopRightRadius: 'inherit', borderTopLeftRadius: 'inherit' }}>
        <Stack direction='row' sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {titleText}
          {dismissable && (
            <IconButton sx={(theme) => getColorsForVariant(theme, variant)} onClick={handleClose}>
              <CloseIcon />
            </IconButton>
          )}
        </Stack>
      </DialogTitle>
      {children}
    </Dialog>
  );
};
